import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export interface LearningFeedback {
  id: string;
  user_id: string;
  bill_id?: string;
  queue_item_id?: string;
  feedback_type: 'correction' | 'validation';
  original_extraction: any;
  corrected_extraction: any;
  confidence_before?: number;
  confidence_after?: number;
  field_corrections: any;
  created_at: string;
}

interface CreateFeedbackData {
  bill_id?: string;
  queue_item_id?: string;
  feedback_type: 'correction' | 'validation';
  original_extraction: any;
  corrected_extraction: any;
  confidence_before?: number;
  field_corrections?: any;
  notes?: string;
}

export const useAILearningFeedback = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch learning feedback
  const feedbackQuery = useQuery({
    queryKey: ['ai-learning-feedback', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('ai_learning_feedback')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  // Create feedback mutation
  const createFeedbackMutation = useMutation({
    mutationFn: async (feedbackData: CreateFeedbackData) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('ai_learning_feedback')
        .insert({
          user_id: user.id,
          ...feedbackData,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-learning-feedback'] });
      toast({
        title: "Feedback Submitted",
        description: "Your feedback will help improve AI accuracy.",
      });
    },
    onError: (error) => {
      console.error('Error creating feedback:', error);
      toast({
        title: "Error",
        description: "Failed to submit feedback. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Get feedback statistics
  const getFeedbackStats = () => {
    const feedback = feedbackQuery.data || [];
    
    return {
      totalFeedback: feedback.length,
      corrections: feedback.filter(f => f.feedback_type === 'correction').length,
      validations: feedback.filter(f => f.feedback_type === 'validation').length,
      avgConfidenceImprovement: feedback.reduce((acc, f) => {
        if (f.confidence_before && f.confidence_after) {
          return acc + (f.confidence_after - f.confidence_before);
        }
        return acc;
      }, 0) / feedback.filter(f => f.confidence_before && f.confidence_after).length || 0,
    };
  };

  // Apply learning from feedback to improve future extractions
  const applyLearning = async (extractedData: any, similarFeedback: LearningFeedback[]) => {
    if (similarFeedback.length === 0) return extractedData;

    const improvedData = { ...extractedData };
    
    // Apply field-level corrections based on historical feedback
    similarFeedback.forEach(feedback => {
      if (feedback.field_corrections) {
        Object.entries(feedback.field_corrections).forEach(([field, correction]) => {
          // Apply pattern-based learning
          if (improvedData[field] && typeof correction === 'string') {
            // Simple similarity check for learning
            const originalExtraction = feedback.original_extraction as any;
            const originalValue = originalExtraction[field];
            const currentValue = improvedData[field];
            
            if (originalValue && currentValue && 
                originalValue.toLowerCase().includes(currentValue.toLowerCase().substring(0, 3))) {
              improvedData[field] = correction;
              
              // Increase confidence if we applied a learned correction
              if (improvedData.ai_confidence) {
                improvedData.ai_confidence = Math.min(improvedData.ai_confidence + 0.1, 1.0);
              }
            }
          }
        });
      }
    });

    return improvedData;
  };

  // Find similar feedback for learning
  const findSimilarFeedback = (extractedData: any) => {
    const feedback = feedbackQuery.data || [];
    
    return feedback.filter(f => {
      // Simple similarity check based on supplier or customer name
      const originalExtraction = f.original_extraction as any;
      const originalSupplier = originalExtraction?.supplier_name?.toLowerCase();
      const currentSupplier = extractedData?.supplier_name?.toLowerCase();
      
      const originalCustomer = originalExtraction?.customer_name_raw?.toLowerCase();
      const currentCustomer = extractedData?.customer_name_raw?.toLowerCase();
      
      return (originalSupplier && currentSupplier && originalSupplier.includes(currentSupplier.substring(0, 5))) ||
             (originalCustomer && currentCustomer && originalCustomer.includes(currentCustomer.substring(0, 5)));
    });
  };

  return {
    feedback: feedbackQuery.data || [],
    isLoading: feedbackQuery.isLoading,
    error: feedbackQuery.error,
    createFeedback: createFeedbackMutation.mutate,
    isCreating: createFeedbackMutation.isPending,
    getFeedbackStats,
    applyLearning,
    findSimilarFeedback,
  };
};